<script setup lang="ts">
import { computed } from 'vue'
import LiquidityChart from './LiquidityChart.vue'
import { useFormat } from '../composables/useFormat.js'
import { TF_ORDER, levelPillText } from '../composables/liquidityLevelStyles.js'

type PointRow = {
  id: number
  side: string
  side_label: string
  price: number
  status: string
  status_label: string
  mtf_label?: string | null
  url: string
}

type ZoneRow = {
  side: string
  price: number
  timeframe: string
  pct_of_max: number
  distance_pct: number
  blocked?: boolean
  block_id?: number | null
}

const props = defineProps<{
  exchange: string
  symbol: string
  label: string
  lastPrice: number | null
  zones: any
  error: string | null
  longs: PointRow[]
  shorts: PointRow[]
  timeframe: string
  apiBase: string
  chartHeight: number
  refreshMs: number
  compactChrome: boolean
  zoneBusy: boolean
}>()

const emit = defineEmits<{
  block: [payload: { timeframe: string; side: string; price: number }]
  unblock: [payload: ZoneRow]
  points: []
  expand: []
  grip: [event: PointerEvent]
}>()

const { price } = useFormat()

const chartKey = computed(() => `${props.symbol}:${props.timeframe}`)
const pointCount = computed(() => props.longs.length + props.shorts.length)

const zoneRows = computed<ZoneRow[]>(() => {
  const data = props.zones?.timeframes
  if (!data) return []
  const rows: ZoneRow[] = []
  for (const tf of TF_ORDER) {
    const frame = data[tf]
    if (!frame) continue
    for (const level of frame.longs || []) {
      rows.push({ ...level, side: 'long', timeframe: tf })
    }
    for (const level of frame.shorts || []) {
      rows.push({ ...level, side: 'short', timeframe: tf })
    }
  }
  return rows.sort((a, b) => b.price - a.price)
})

function formatDistance(pct: number) {
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(2)}%`
}
</script>

<template>
  <article class="board-tile" :class="{ 'board-tile--fs': compactChrome }" dir="ltr">
    <header class="board-tile__head">
      <button
        type="button"
        class="board-tile__grip"
        :aria-label="__('Drag to reorder')"
        :title="__('Drag to reorder')"
        @pointerdown.stop="emit('grip', $event)"
      >
        <span />
        <span />
        <span />
      </button>
      <div class="board-tile__id min-w-0">
        <h3 class="board-tile__title">
          {{ label }}
          <span class="board-tile__sym">{{ symbol }}</span>
        </h3>
        <p v-if="lastPrice != null" class="board-tile__price">{{ price(lastPrice) }}</p>
      </div>
      <div class="board-tile__actions">
        <span v-if="!compactChrome" class="board-tile__badges" :title="__('Main points')">
          <span class="board-tile__badge board-tile__badge--long">L {{ longs.length }}</span>
          <span class="board-tile__badge board-tile__badge--short">S {{ shorts.length }}</span>
        </span>
        <button
          type="button"
          class="board-tile__expand"
          :aria-label="__('Expand chart')"
          :title="__('Expand chart')"
          @click="emit('expand')"
        >
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <path
              fill="currentColor"
              d="M2 2h5v1.5H3.5V7H2V2zm7 0h5v5h-1.5V3.5H9V2zM2 9h1.5v3.5H7V14H2V9zm10.5 0H14v5H9v-1.5h3.5V9z"
            />
          </svg>
        </button>
        <button
          type="button"
          class="board-tile__points"
          :aria-label="__('Main points')"
          @click="emit('points')"
        >
          {{ __('Points') }}
          <span v-if="pointCount" class="board-tile__count">{{ pointCount }}</span>
        </button>
      </div>
    </header>

    <p v-if="error" class="board-tile__error">{{ error }}</p>

    <LiquidityChart
      :key="chartKey"
      compact
      :exchange="exchange"
      :symbol="symbol"
      :timeframe="timeframe"
      :levels="zones"
      :api-base="apiBase"
      :height="chartHeight"
      :refresh-ms="refreshMs"
      :candle-limit="1000"
      @block="emit('block', $event)"
    />

    <div v-if="!compactChrome" class="board-tile__zones">
      <p v-if="!zoneRows.length" class="board-tile__empty">{{ __('No zones at this threshold.') }}</p>
      <div v-else class="board-tile__table-wrap">
        <table class="board-tile__table">
          <thead>
            <tr>
              <th>{{ __('Side') }}</th>
              <th>{{ __('Price') }}</th>
              <th>TF</th>
              <th>{{ __('Action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in zoneRows"
              :key="`${row.timeframe}-${row.side}-${row.price}-${i}`"
              :class="{ 'is-blocked': row.blocked }"
            >
              <td :class="row.side === 'short' ? 'is-short' : 'is-long'">
                {{ row.side === 'short' ? __('Short') : __('Long') }}
              </td>
              <td class="font-mono">
                {{ levelPillText(row.timeframe, row.price) }}
                <span class="board-tile__dist">{{ formatDistance(row.distance_pct) }}</span>
              </td>
              <td class="font-mono text-zinc-500">{{ row.timeframe }}</td>
              <td class="text-end">
                <button
                  v-if="row.blocked"
                  type="button"
                  class="board-tile__zone-btn"
                  :disabled="zoneBusy"
                  @click="emit('unblock', row)"
                >
                  {{ __('Unblock') }}
                </button>
                <button
                  v-else
                  type="button"
                  class="board-tile__zone-btn board-tile__zone-btn--block"
                  :disabled="zoneBusy"
                  @click="emit('block', row)"
                >
                  {{ __('Block') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </article>
</template>

<style scoped>
.board-tile {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border-radius: 1.15rem;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(161, 161, 170, 0.22);
}
:global(html.dark) .board-tile {
  background: rgba(19, 23, 34, 0.72);
  border-color: #2a2e39;
}
.board-tile--fs {
  border-radius: 0.65rem;
  background: #131722;
}
.board-tile__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem 0.4rem;
}
.board-tile__grip {
  flex-shrink: 0;
  display: grid;
  gap: 3px;
  width: 2.25rem;
  height: 2.25rem;
  place-content: center;
  border: 0;
  border-radius: 0.55rem;
  background: rgba(255, 255, 255, 0.06);
  cursor: grab;
  touch-action: none;
}
.board-tile__grip:active {
  cursor: grabbing;
}
.board-tile__grip span {
  display: block;
  width: 14px;
  height: 2px;
  border-radius: 99px;
  background: #8b919d;
}
.board-tile__id {
  flex: 1;
}
.board-tile--fs .board-tile__head {
  padding: 0.28rem 0.45rem;
}
.board-tile__title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  color: inherit;
  line-height: 1.15;
}
.board-tile__sym {
  margin-inline-start: 0.35rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.68rem;
  font-weight: 500;
  color: #a1a1aa;
}
.board-tile__price {
  margin: 0.1rem 0 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  color: #71717a;
}
.board-tile__actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}
.board-tile__badges {
  display: flex;
  gap: 0.25rem;
}
.board-tile__badge {
  border-radius: 999px;
  padding: 0.1rem 0.4rem;
  font-size: 10px;
  font-weight: 700;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.board-tile__badge--long {
  color: #10b981;
  background: rgba(16, 185, 129, 0.12);
}
.board-tile__badge--short {
  color: #f43f5e;
  background: rgba(244, 63, 94, 0.12);
}
.board-tile__points {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  border-radius: 999px;
  border: 1px solid rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.12);
  color: #60a5fa;
  padding: 0.22rem 0.6rem;
  font-size: 11px;
  font-weight: 700;
}
.board-tile__expand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 0.55rem;
  border: 1px solid rgba(210, 213, 220, 0.18);
  background: rgba(255, 255, 255, 0.06);
  color: #d1d4dc;
}
.board-tile__expand:hover {
  background: rgba(41, 98, 255, 0.18);
  border-color: rgba(41, 98, 255, 0.45);
  color: #93c5fd;
}
.board-tile__count {
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.22);
  padding: 0 0.35rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.board-tile__error {
  margin: 0 0.6rem 0.35rem;
  font-size: 11px;
  color: #fb7185;
}
.board-tile__zones {
  min-height: 0;
  max-height: 8.25rem;
  overflow: auto;
  border-top: 1px solid rgba(42, 46, 57, 0.55);
}
.board-tile__empty {
  margin: 0;
  padding: 0.7rem 0.75rem;
  font-size: 11px;
  color: #71717a;
}
.board-tile__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  text-align: start;
}
.board-tile__table th {
  padding: 0.35rem 0.5rem;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #71717a;
}
.board-tile__table td {
  padding: 0.28rem 0.5rem;
  border-top: 1px solid rgba(161, 161, 170, 0.12);
}
.board-tile__table .is-long {
  color: #10b981;
  font-weight: 650;
}
.board-tile__table .is-short {
  color: #f43f5e;
  font-weight: 650;
}
.board-tile__table .is-blocked {
  opacity: 0.55;
}
.board-tile__dist {
  margin-inline-start: 0.35rem;
  color: #71717a;
}
.board-tile__zone-btn {
  border-radius: 999px;
  border: 1px solid rgba(161, 161, 170, 0.35);
  background: transparent;
  padding: 0.12rem 0.5rem;
  font-size: 10px;
  font-weight: 700;
  color: inherit;
}
.board-tile__zone-btn--block {
  border-color: rgba(244, 63, 94, 0.4);
  color: #fb7185;
}
</style>
